import { useThree } from '@react-three/fiber';
import {
    useEffect,
    useMemo,
    useRef,
    useState,
} from 'react';
import {
    getShadow,
    getShadowCacheSize,
    storeShadow,
} from './cache';
import type { ShadowCacheEntry } from './cache';
import {
    PROGRESSIVE_SHADOW_DELAY_MS,
    PROGRESSIVE_SHADOW_INITIAL_DELAY_MS,
    PROGRESSIVE_SHADOW_UPDATES,
    SHADOW_BAKE_ACTIVE_USER_DATA_KEY,
    SHADOW_UPDATES,
} from './config';
import type { ShadowBakerHandle } from './ShadowBaker';
import {
    captureShadowTexture,
    createShadowCaptureResources,
    disposeShadowCaptureResources,
} from './shadowCapture';
import type {
    ShadowCacheStatus,
    ShadowModel,
} from './types';
import { renderReadiness } from '../Rendering/browserReadiness';

export interface ActiveShadow {
    entry: ShadowCacheEntry
    modelRevision?: number
    status: ShadowCacheStatus
    bakeTimeMs: number
}

/**
 * Resolves a model's cached shadow or drives Drei's manual accumulative bake.
 *
 * The hidden baker is allowed to render only while update() is running. Once
 * complete, its reusable render target is copied into an independent R8
 * texture and published to the page-lifetime cache.
 */
export default function useBakedShadow(
    model:ShadowModel|undefined,
    progressive:boolean,
) {
    const renderer = useThree(state => state.gl);
    const invalidate = useThree(state => state.invalidate);
    const scene = useThree(state => state.scene);
    const shadowsRef = useRef<ShadowBakerHandle>(null);
    const captureResources = useMemo(createShadowCaptureResources, []);
    const [activeShadow, setActiveShadow] = useState<ActiveShadow|null>(null);

    const cacheKey = model?.cacheKey;
    const modelRevision = model?.modelRevision;
    const activeShadowMatchesModel = activeShadow !== null
        && activeShadow.entry.cacheKey === cacheKey
        && activeShadow.modelRevision === modelRevision;
    const visibleShadow = activeShadowMatchesModel ? activeShadow : null;

    useEffect(() => {
        let cancelled = false;
        let firstFrame = 0;
        let secondFrame = 0;
        let bakeTimer = 0;

        if(!model) {
            setActiveShadow(currentShadow => currentShadow === null ? currentShadow : null);
            return;
        }

        // Cache identity describes geometry/options only; modelRevision is
        // attached to the active result so the current Puppeteer request can
        // still acknowledge a reused bake.
        const cachedEntry = getShadow(model.cacheKey);
        if(cachedEntry) {
            console.info(
                `[ModelViewer shadow cache] hit "${model.cacheKey}" `
                + `(${getShadowCacheSize()} cached)`,
            );
            setActiveShadow({
                bakeTimeMs: 0,
                entry: cachedEntry,
                modelRevision: model.modelRevision,
                status: 'hit',
            });
            return;
        }

        setActiveShadow(currentShadow => currentShadow === null ? currentShadow : null);

        // Stage reports centered bounds before all descendants necessarily have
        // rendered. Wait two frames, then recheck in case another viewer filled
        // the same cache entry while this one was settling.
        firstFrame = requestAnimationFrame(() => {
            secondFrame = requestAnimationFrame(() => {
                if(cancelled) return;

                const entryBakedByEarlierEffect = getShadow(model.cacheKey);
                if(entryBakedByEarlierEffect) {
                    setActiveShadow({
                        bakeTimeMs: 0,
                        entry: entryBakedByEarlierEffect,
                        modelRevision: model.modelRevision,
                        status: 'hit',
                    });
                    return;
                }

                const shadows = shadowsRef.current;
                if(!shadows) {
                    renderReadiness.fail(
                        'Shadow baker was not initialized',
                    );
                    console.error('[ModelViewer shadow cache] Shadow baker was not initialized');
                    return;
                }

                const bakeStartedAt = performance.now();
                const shadowMesh = shadows.getMesh();
                const sceneUserData = scene.userData as Record<string, unknown>;
                // Reflections and transmission inspect this scene flag and
                // suppress their own offscreen passes during accumulation.
                const updateShadows = (frames:number) => {
                    const previousBakeState = (
                        sceneUserData[SHADOW_BAKE_ACTIVE_USER_DATA_KEY]
                    );
                    sceneUserData[SHADOW_BAKE_ACTIVE_USER_DATA_KEY] = true;

                    try {
                        shadows.update(frames);
                    } finally {
                        sceneUserData[SHADOW_BAKE_ACTIVE_USER_DATA_KEY] = (
                            previousBakeState
                        );
                    }
                };
                const reportBakeError = (error:unknown) => {
                    const message = error instanceof Error ? error.message : String(error);
                    renderReadiness.fail(
                        `Shadow bake failed: ${message}`,
                    );
                    console.error('[ModelViewer shadow cache] Shadow bake failed', error);
                };
                const completeBake = () => {
                    if(cancelled) return;

                    try {
                        const shadowMap = shadowMesh.material.map;
                        const texture = captureShadowTexture(
                            renderer,
                            shadowMap,
                            captureResources,
                            model.cacheKey,
                        );
                        const entry = storeShadow({
                            cacheKey: model.cacheKey,
                            texture,
                        });
                        const bakeTimeMs = performance.now() - bakeStartedAt;

                        console.info(
                            `[ModelViewer shadow cache] miss "${model.cacheKey}", `
                            + `baked in ${bakeTimeMs.toFixed(1)}ms `
                            + `(${getShadowCacheSize()} cached)`,
                        );

                        setActiveShadow({
                            bakeTimeMs,
                            entry,
                            modelRevision: model.modelRevision,
                            status: 'miss',
                        });
                    } catch(error) {
                        reportBakeError(error);
                    }
                };

                try {
                    shadows.reset();
                    shadowMesh.visible = false;

                    if(progressive) {
                        // Interactive Studio spreads work across timers to keep
                        // controls responsive. Static renders bake the full
                        // sample count synchronously for deterministic output.
                        let updates = 0;
                        const updateShadow = () => {
                            if(cancelled) return;

                            try {
                                shadowMesh.visible = true;
                                updateShadows(1);
                                updates++;
                            } catch(error) {
                                reportBakeError(error);
                                return;
                            } finally {
                                shadowMesh.visible = false;
                            }

                            invalidate();
                            if(updates < PROGRESSIVE_SHADOW_UPDATES) {
                                bakeTimer = window.setTimeout(
                                    updateShadow,
                                    PROGRESSIVE_SHADOW_DELAY_MS,
                                );
                            } else {
                                completeBake();
                            }
                        };

                        bakeTimer = window.setTimeout(
                            updateShadow,
                            PROGRESSIVE_SHADOW_INITIAL_DELAY_MS,
                        );
                    } else {
                        shadowMesh.visible = true;
                        updateShadows(SHADOW_UPDATES);
                        shadowMesh.visible = false;
                        completeBake();
                    }
                } catch(error) {
                    shadowMesh.visible = false;
                    reportBakeError(error);
                }
            });
        });

        return () => {
            cancelled = true;
            cancelAnimationFrame(firstFrame);
            cancelAnimationFrame(secondFrame);
            clearTimeout(bakeTimer);
        };
    }, [
        captureResources,
        invalidate,
        model,
        progressive,
        renderer,
        scene,
    ]);

    useEffect(() => {
        return () => disposeShadowCaptureResources(captureResources);
    }, [captureResources]);

    return {
        scratchVisible: model !== undefined && !activeShadowMatchesModel,
        shadowsRef,
        visibleShadow,
    };
}
